import cx from "classnames";

import Icon from "common/components/Icon";
import LinkButton from "common/components/LinkButton";
import CategoryCardBlob from "common/components/CategoryCardBlob";
import { noop } from "common/utils/noop";
import { iconForCategorySlug } from "common/constants/categoryIcons";
import { gradientForScheme, schemeForCategoryIndex } from "common/constants/carousels";

import PillButton from "client/common/components/buttons/PillButton";
import { urls } from "client/routes";

import { IClientBrowseCategory } from "../types";
import { DEALS_CATEGORY } from "./CategoryCard";

import styles from "../styles/CategoryCard.module.less";

// CategoryCardV2 reads only id / slug / name / description from category, matching
// CategoryCard so it accepts the carousel-display category without the heavier
// product_lines payload. `index` is the 0-based cycle position used to pick the color.
type CategoryCardV2Category = Pick<IClientBrowseCategory, "id" | "slug" | "name" | "description">;

type CategoryCardV2Props = {
  category: CategoryCardV2Category;
  index: number;
  className?: string;
};

const getCategoryDestination = (category: CategoryCardV2Category) => {
  if (category.slug === DEALS_CATEGORY.slug) {
    return urls.products.deals();
  }
  return urls.products.category({ params: { id: category.id } });
};

// The small "popular categories" strip keeps the white card background (per product);
// only the blob and icon take the position-based scheme color. The full section card
// (below) carries the scheme gradient.
const CategoryCardV2Small = ({ category, index, className }: CategoryCardV2Props) => {
  const scheme = schemeForCategoryIndex(index);

  return (
    <LinkButton
      className={cx(styles.small, category.slug === DEALS_CATEGORY.slug && styles.deals, className)}
      key={category.id}
      dest={getCategoryDestination(category)}
    >
      <div className={styles.name}>{category.name}</div>
      <div>
        <CategoryCardBlob fill={scheme.blob} className={styles.blob} />
        <Icon
          className={styles.icon}
          icon={iconForCategorySlug(category.slug)}
          color={scheme.icon}
        />
      </div>
    </LinkButton>
  );
};

const CategoryCardV2 = ({ category, index, className }: CategoryCardV2Props) => {
  const scheme = schemeForCategoryIndex(index);

  return (
    <LinkButton
      dest={getCategoryDestination(category)}
      className={cx(styles.full, className)}
      style={{ background: gradientForScheme(scheme) }}
    >
      <div className={styles.content}>
        <div className={styles.name}>{category.name}</div>
        <div className={styles.description}>{category.description}</div>
        <PillButton size="xs" variant="secondary" onClick={noop}>
          View all
        </PillButton>
      </div>
      <div>
        <CategoryCardBlob fill={scheme.blob} className={styles.blob} />
        <Icon
          className={styles.icon}
          icon={iconForCategorySlug(category.slug)}
          color={scheme.icon}
        />
      </div>
    </LinkButton>
  );
};

export default Object.assign(CategoryCardV2, {
  Small: CategoryCardV2Small,
});
